import { elementAttributes } from "../html-attributes";
import { INLINE_TAGS } from "../html-fallback/claim";
import {
  parseImageStyle,
  parseImageWidth,
  parseTextAlign,
} from "../html-parse";
import { EMPTY_STYLE_MAP, type StyleMap } from "../shared-block-styles";
import { EmailBlockDefinition } from "../types";

import { LuImage } from "react-icons/lu";

import { imageEmailRenderer } from "./email-renderer";
import { ImageTiptapView } from "./tiptap-view";

export interface ImageAttrs extends Record<string, unknown> {
  src: string;
  alt: string;
  width: "auto" | "25%" | "50%" | "75%" | "100%";
  alignment: "left" | "center" | "right";
  /** Optional max height constraint (e.g. "36px"). */
  maxHeight: string;
  /** Optional destination URL that makes the image clickable in rendered email. */
  href: string;
  /** True while an upload is in-flight (paste/drop/click). */
  uploading: boolean;
  /** Unique ID to match an upload callback to its placeholder node. */
  uploadId: string;
  /** Natural pixel dimensions — used to size the upload skeleton. */
  naturalWidth: number;
  naturalHeight: number;
  /** Inline styles as the markup wrote them — the block's style store. */
  style: StyleMap;
  /** Attributes the block does not otherwise model, kept rather than dropped. */
  attributes: Record<string, string>;
}

// Inline wrappers only, so an outer centred cell can't re-centre a nested left image.
function alignmentSource(image: HTMLElement): HTMLElement {
  let element = image.parentElement ?? image;
  while (
    INLINE_TAGS.has(element.tagName.toLowerCase()) &&
    !parseTextAlign(element) &&
    element.parentElement
  ) {
    element = element.parentElement;
  }
  return element;
}

export const imageBlock: EmailBlockDefinition<ImageAttrs> = {
  name: "emailImage",
  label: "Image",
  icon: LuImage,
  category: "media",

  schema: {
    attrs: {
      src: { default: "" },
      alt: { default: "" },
      width: { default: "100%" },
      alignment: { default: "center" },
      maxHeight: { default: "" },
      href: { default: "" },
      uploading: { default: false },
      uploadId: { default: "" },
      naturalWidth: { default: 0 },
      naturalHeight: { default: 0 },
      style: { default: EMPTY_STYLE_MAP },
      attributes: { default: {} },
    },
    content: undefined,
    group: "emailBlock",
    draggable: true,
    isolating: true,
  },

  // An email logo is usually `<a href><img></a>`; read the wrapping link into
  // `href` so the image stays clickable instead of losing its destination.
  parseHTML: () => [
    {
      tag: "img[src]",
      getAttrs: (node) => {
        const el = node as HTMLElement;
        const link = el.parentElement?.closest("a[href]");
        return {
          src: el.getAttribute("src") ?? "",
          alt: el.getAttribute("alt") ?? "",
          width: parseImageWidth(el),
          alignment: parseTextAlign(alignmentSource(el)),
          maxHeight: el.style.maxHeight,
          href: link?.getAttribute("href") ?? "",
          style: parseImageStyle(el),
          attributes: elementAttributes(el, [
            "src",
            "alt",
            "width",
            "height",
            "align",
          ]),
        };
      },
    },
  ],

  tiptapComponent: ImageTiptapView,
  emailRenderer: imageEmailRenderer,
};
